import { type ReactNode, createContext, useContext, useSyncExternalStore } from "react";
import type { ApiClient } from "../api/client.js";
import type { KeyValueStore } from "../lib/storage.js";
import type { Session, SessionStore } from "./session.js";

export interface Services {
  api: ApiClient;
  sessions: SessionStore;
  /** Per-browser preferences that no other member needs to see. */
  store: KeyValueStore;
}

const ServicesContext = createContext<Services | null>(null);

export function ServicesProvider({ services, children }: { services: Services; children: ReactNode }) {
  return <ServicesContext.Provider value={services}>{children}</ServicesContext.Provider>;
}

function useServices(): Services {
  const services = useContext(ServicesContext);
  if (services === null) throw new Error("ServicesProvider is missing");
  return services;
}

export function useApi(): ApiClient {
  return useServices().api;
}

export function useSessions(): SessionStore {
  return useServices().sessions;
}

export function useStore(): KeyValueStore {
  return useServices().store;
}

export function useSession(): Session | null {
  const sessions = useSessions();
  return useSyncExternalStore(sessions.subscribe, sessions.get);
}
